import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import USMap from "../../components/licensePlate/USMap";
import { SYM, withEllipsis } from "../../utils/textSymbols";
import { STATE_QUARTER_NAMES, isValidStateQuarterCode } from "../../data/stateQuarterStates.js";
import StateQuarterGallery from "./StateQuarterGallery";
import "../../pages/LicensePlateGamePage.css";
import "./StateQuartersGamePage.css";

const TABS = [
  { id: "Map", lines: ["Map"] },
  { id: "Leaderboard", lines: ["Leader", "board"] }
];

function solidMemberColor(color) {
  if (!color || typeof color !== "string") return "#2d74da";
  return color.startsWith("#") ? color.slice(0, 7) : `#${color}`.slice(0, 7);
}

function memberChipStyle(color) {
  const bg = solidMemberColor(color);
  return { backgroundColor: bg, borderColor: bg, color: "#111" };
}

function memberLabelStyle(color) {
  const bg = solidMemberColor(color);
  return { backgroundColor: bg, borderColor: "rgba(0, 0, 0, 0.12)", color: "#111" };
}

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function StateQuartersGamePage({ apiBase, familyMembers, onBack }) {
  const [tab, setTab] = useState("Map");
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pickState, setPickState] = useState(null);
  const pickModalOpenedAtRef = useRef(0);
  const PICK_MODAL_CLICK_GUARD_MS = 400;
  const [submitting, setSubmitting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [menuModal, setMenuModal] = useState(null);
  const [findDates, setFindDates] = useState({});
  const [celebration, setCelebration] = useState(null);
  const clearCelebration = useCallback(() => setCelebration(null), []);
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark"
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  const members = useMemo(() => {
    const list = (familyMembers || []).filter((m) => m.id && m.id !== "all" && m.name);
    const seen = new Set();
    return list.filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [familyMembers]);

  const memberColors = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.id] = m.color || "#2d74da"; });
    return map;
  }, [members]);

  const memberNameById = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.id] = m.name; });
    return map;
  }, [members]);

  const applyGameResponse = useCallback((data) => {
    setGame(data);
    const dates = {};
    (data.history || []).forEach((row) => {
      dates[row.id] = toLocalInputValue(row.foundAt);
    });
    setFindDates(dates);
  }, []);

  const fetchGame = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/GetStateQuartersGame`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      applyGameResponse(data);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [apiBase, applyGameResponse]);

  useEffect(() => {
    fetchGame();
  }, [fetchGame]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [tab]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  const claims = game?.claims || {};
  const pickStateClaim = pickState ? claims[pickState] : null;

  const openPickModal = (stateCode) => {
    if (!isValidStateQuarterCode(stateCode)) {
      setActionError("Only the 50 state quarters are part of this game (no DC or territories).");
      return;
    }
    pickModalOpenedAtRef.current = Date.now();
    setPickState(stateCode);
    setActionError(null);
  };

  const handlePlayerPick = (member, e) => {
    if (Date.now() - pickModalOpenedAtRef.current < PICK_MODAL_CLICK_GUARD_MS) {
      e?.preventDefault?.();
      return;
    }
    recordFind(member);
  };

  const recordFind = async (member) => {
    if (!pickState || !member) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/RecordStateQuarterFind`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stateCode: pickState,
          foundBy: member.id,
          foundByName: member.name
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      const stateCode = pickState;
      applyGameResponse(data);
      setPickState(null);
      setCelebration({ code: stateCode, color: member.color || "#2d74da" });
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openMenuModal = (id) => {
    setMenuModal(id);
    setMenuOpen(false);
    setActionError(null);
  };

  const handleUpdateFindDate = async (findId) => {
    const value = findDates[findId];
    if (!value) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/UpdateStateQuarterFind`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ findId, foundAt: new Date(value).toISOString() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      applyGameResponse(data);
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFind = async (findId, label) => {
    if (!window.confirm(`Remove find for ${label}?`)) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/DeleteStateQuarterFind`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ findId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      applyGameResponse(data);
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const leaderboard = game?.leaderboard || [];
  const foundCount = game?.foundCount || 0;
  const totalStates = game?.totalStates || 50;

  return (
    <div className={`lp-page sq-page ${tab === "Map" ? "lp-page-map" : ""}`}>
      <header className="lp-header">
        <button type="button" className="lp-back-btn" onClick={onBack}>
          <span className="lp-back-arrow" aria-hidden="true">{SYM.arrowLeft}</span>
          <span className="lp-back-label">Games</span>
        </button>
        <h1 className="lp-title">State Quarters</h1>
        <div className="lp-header-menu" ref={menuRef}>
          <button
            type="button"
            className="lp-menu-btn"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {SYM.menu}
          </button>
          {menuOpen && (
            <div className="lp-menu-panel" role="menu">
              <button type="button" className="lp-menu-item" role="menuitem" onClick={() => openMenuModal("finds")}>
                Edit finds
              </button>
              <button type="button" className="lp-menu-item" role="menuitem" onClick={() => openMenuModal("history")}>
                History
              </button>
            </div>
          )}
        </div>
      </header>

      <nav className="lp-tabs" aria-label="Game sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lp-tab-btn ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <span className="lp-tab-label">
              {t.lines.map((line) => (
                <span key={line} className="lp-tab-line">{line}</span>
              ))}
            </span>
          </button>
        ))}
      </nav>

      {loading && <p className="lp-status">{withEllipsis("Loading")}</p>}
      {error && <p className="lp-error">{error}</p>}
      {actionError && <p className="lp-error">{actionError}</p>}

      {!loading && !error && tab === "Map" && (
        <section className="lp-map-section">
          <div className="sq-progress">
            <span className="sq-progress-count">{foundCount} / {totalStates}</span>
            <span className="sq-progress-label">quarters found</span>
          </div>
          <USMap
            claims={claims}
            memberColors={memberColors}
            onStateClick={openPickModal}
            celebration={celebration}
            onCelebrationComplete={clearCelebration}
            dark={isDark}
            showOther={false}
            claimerIdKey="foundBy"
          />
        </section>
      )}

      {!loading && !error && tab === "Leaderboard" && (
        <section className="lp-section">
          <p className="sq-leaderboard-note">First to find each state quarter gets the credit. Map stays until you clear a find.</p>
          {leaderboard.length === 0 && <p className="lp-empty">No finds yet</p>}
          <ol className="lp-leaderboard">
            {leaderboard.map((entry, i) => (
              <li key={entry.foundBy} className="lp-leader-row">
                <span className="lp-rank">{i + 1}</span>
                <span className="lp-player" style={memberLabelStyle(memberColors[entry.foundBy])}>
                  {entry.foundByName || memberNameById[entry.foundBy] || entry.foundBy}
                </span>
                <span className="lp-score">{entry.score}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {menuModal === "finds" && (
        <div className="lp-modal-backdrop" onClick={() => !submitting && setMenuModal(null)}>
          <div className="lp-modal lp-menu-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="lp-modal-title">Edit finds</h2>
            {(game?.history || []).length === 0 && <p className="lp-empty">No finds yet</p>}
            <ul className="lp-correction-list">
              {(game?.history || []).map((row) => (
                <li key={row.id} className="lp-correction-row">
                  <div className="lp-correction-meta">
                    <strong>{row.stateCode} {SYM.enDash} {STATE_QUARTER_NAMES[row.stateCode] || row.stateCode}</strong>
                    <span>{row.foundByName || memberNameById[row.foundBy] || row.foundBy}</span>
                  </div>
                  <div className="lp-correction-actions">
                    <input
                      type="datetime-local"
                      className="lp-input"
                      value={findDates[row.id] || ""}
                      onChange={(e) => setFindDates((prev) => ({ ...prev, [row.id]: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="lp-secondary-btn"
                      disabled={submitting}
                      onClick={() => handleUpdateFindDate(row.id)}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="lp-danger-btn"
                      disabled={submitting}
                      onClick={() => handleDeleteFind(row.id, STATE_QUARTER_NAMES[row.stateCode] || row.stateCode)}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <button type="button" className="lp-cancel-btn" onClick={() => setMenuModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {menuModal === "history" && (
        <div className="lp-modal-backdrop" onClick={() => setMenuModal(null)}>
          <div className="lp-modal lp-menu-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="lp-modal-title">History</h2>
            {(game?.history || []).length === 0 && <p className="lp-empty">No finds yet</p>}
            <div className="lp-history-scroll">
              <table className="lp-history-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Finder</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {(game?.history || []).map((row) => (
                    <tr key={row.id}>
                      <td>{row.stateCode} {SYM.enDash} {STATE_QUARTER_NAMES[row.stateCode] || row.stateCode}</td>
                      <td>{row.foundByName || memberNameById[row.foundBy] || row.foundBy}</td>
                      <td>{row.foundAt ? new Date(row.foundAt).toLocaleString() : SYM.emDash}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="lp-cancel-btn" onClick={() => setMenuModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {pickState && pickStateClaim && (
        <div className="lp-modal-backdrop" onClick={() => setPickState(null)}>
          <div
            className="lp-modal lp-modal-spotted"
            style={{ borderColor: memberColors[pickStateClaim.foundBy] || "#2d74da" }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h2 className="lp-modal-title">{STATE_QUARTER_NAMES[pickState] || pickState}</h2>
            <StateQuarterGallery stateCode={pickState} />
            <div
              className="lp-spot-claim"
              style={memberLabelStyle(memberColors[pickStateClaim.foundBy])}
            >
              <span className="lp-spot-claim-name">
                {pickStateClaim.foundByName || memberNameById[pickStateClaim.foundBy] || pickStateClaim.foundBy}
              </span>
              {pickStateClaim.foundAt && (
                <span className="lp-spot-claim-when">
                  {new Date(pickStateClaim.foundAt).toLocaleString()}
                </span>
              )}
              <span className="lp-spot-claim-context">Found</span>
            </div>
            <button type="button" className="lp-cancel-btn" onClick={() => setPickState(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {pickState && !pickStateClaim && (
        <div className="lp-modal-backdrop" onClick={() => !submitting && setPickState(null)}>
          <div className="lp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="lp-modal-title">{STATE_QUARTER_NAMES[pickState] || pickState}</h2>
            <StateQuarterGallery stateCode={pickState} />
            <p className="lp-modal-sub">Who found it?</p>
            {members.length === 0 && (
              <p className="lp-empty">No family members loaded.</p>
            )}
            <div className="lp-player-grid">
              {members.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="lp-player-btn"
                  style={memberChipStyle(m.color)}
                  disabled={submitting}
                  onClick={(e) => handlePlayerPick(m, e)}
                >
                  {m.name}
                </button>
              ))}
            </div>
            <button type="button" className="lp-cancel-btn" onClick={() => setPickState(null)} disabled={submitting}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
